import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { IconAnonymousUser, IconHeaderLogo24, IconLogo, IconLucidePanelLeft, IconLucideX } from '@wegic/icon';
import { Message, Modal, ScrollArea, Tooltip } from '@wegic/ui';
import '@wegic/ui/dist/components/Message/index.css';
import '@wegic/ui/dist/components/Modal/index.css';
import '@wegic/ui/dist/components/ScrollArea/index.css';
import '@wegic/ui/dist/components/Tooltip/index.css';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { isMobile } from '@/const/browser';
import { resumeLaunchOrderGenerationAfterLogin } from '@/helpers/launchOrderGeneration';
import { openGuestLoginModal } from '@/helpers/loginModal';
import { getIsLogoutInProgress } from '@/helpers/logoutState';
import { ANONYMOUS_PROJECT_LIMIT } from '@/helpers/project';
import { collect } from '@/modules/Collect';
import { canUseAnonymousProjectMode } from '@/modules/CustomRouter/helper';
import { useProjectLaunchOrder } from '@/pages/Projects/hooks/useProjectLaunchOrder';
import { deleteProject, getRecentProjects, updateProjectDetail } from '@/services/project';
import { getTrashList } from '@/services/project/trash';
import { selectUnlimitedAnonymousProjects, useDebugStore } from '@/stores/debug';
import { ProjectStatus, useProjectStore } from '@/stores/project';
import { useRecentProjectStore } from '@/stores/recentProject';
import { useTrashStore } from '@/stores/trash';
import { useUserStore } from '@/stores/user';
import CollapsedChatHistory from './CollapsedChatHistory';
import { SIDEBAR_HOME_LABEL, SIDEBAR_SECTIONS } from './const';
import styles from './index.module.less';
import NotificationButton from './NotificationButton';
import RecentProjectList from './RecentProjectList';
import UserButton from './UserButton';
function Sidebar(props) {
    const { defaultCollapsed = false, disableNavItemMotion = false, overlayMode = false, projectOverlayMode = false, projectOverlayVisible = true, visible = true, onProjectOverlayClose, onRequestClose } = props;
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const [deleteModalProject, setDeleteModalProject] = useState(null);
    const [renamingProjectId, setRenamingProjectId] = useState(null);
    const recentProjects = useRecentProjectStore((state)=>state.recentProjects);
    const setRecentProjects = useRecentProjectStore((state)=>state.setRecentProjects);
    const removeRecentProject = useRecentProjectStore((state)=>state.removeRecentProject);
    const updateRecentProject = useRecentProjectStore((state)=>state.updateRecentProject);
    const updateRecentProjectTitle = useRecentProjectStore((state)=>state.updateRecentProjectTitle);
    const hasTrashData = useTrashStore((state)=>state.hasTrashData);
    const setHasTrashData = useTrashStore((state)=>state.setHasTrashData);
    const isLogin = useUserStore((state)=>state.isLogin);
    const userId = useUserStore((state)=>{
        var _state_userInfo;
        return (_state_userInfo = state.userInfo) === null || _state_userInfo === void 0 ? void 0 : _state_userInfo.userId;
    });
    const currentProjectId = useProjectStore((state)=>state.projectId);
    const projectStatus = useProjectStore((state)=>state.status);
    const projectPanelMode = useProjectStore((state)=>state.projectPanelMode);
    const setCurrentProjectDetail = useProjectStore((state)=>state.setCurrentProjectDetail);
    const launchOrder = useProjectLaunchOrder();
    const { projectId: activeProjectId } = useParams();
    const isAnonymousMode = canUseAnonymousProjectMode(isLogin);
    const shouldShowTrash = !isAnonymousMode && hasTrashData === true;
    const isProjectRenaming = Boolean(renamingProjectId);
    const isCollapsed = overlayMode || projectOverlayMode ? false : collapsed;
    const unlimitedAnonymousProjects = useDebugStore(selectUnlimitedAnonymousProjects);
    const anonymousProjectLimitLabel = unlimitedAnonymousProjects ? '∞' : ANONYMOUS_PROJECT_LIMIT;
    const anonymousRecentProjectCountLabel = `${recentProjects.length}/${anonymousProjectLimitLabel}`;
    useEffect(()=>{
        if (getIsLogoutInProgress()) {
            return;
        }
        let ignore = false;
        getRecentProjects().then((res)=>{
            if (!ignore) {
                setRecentProjects(res.data || []);
            }
        }).catch((error)=>{
            console.error('Failed to fetch recent projects:', error);
        });
        return ()=>{
            ignore = true;
        };
    }, [
        userId,
        setRecentProjects
    ]);
    useEffect(()=>{
        if (isAnonymousMode || hasTrashData !== null) {
            return;
        }
        let ignore = false;
        getTrashList({
            page: 1,
            pageSize: 1
        }).then((res)=>{
            if (ignore) {
                return;
            }
            setHasTrashData((res.data.list || []).length > 0);
        }).catch((error)=>{
            console.error('Failed to fetch trash entry status:', error);
        });
        return ()=>{
            ignore = true;
        };
    }, [
        hasTrashData,
        isAnonymousMode,
        setHasTrashData
    ]);
    const handleRenameCancel = ()=>{
        setRenamingProjectId(null);
    };
    const handleRenameSave = (project, name)=>{
        const previousCurrentProjectDetail = useProjectStore.getState().currentProjectDetail;
        const isActiveProject = project.id === activeProjectId && (previousCurrentProjectDetail === null || previousCurrentProjectDetail === void 0 ? void 0 : previousCurrentProjectDetail.id) === project.id;
        setRenamingProjectId(null);
        updateRecentProjectTitle(project.id, name);
        if (isActiveProject) {
            setCurrentProjectDetail({
                ...previousCurrentProjectDetail,
                name
            });
        }
        updateProjectDetail(project.id, {
            name
        }).then((res)=>{
            updateRecentProject(project.id, (item)=>({
                    ...item,
                    ...res.data
                }));
            if (isActiveProject) {
                setCurrentProjectDetail({
                    ...previousCurrentProjectDetail,
                    ...res.data
                });
            }
        }).catch((error)=>{
            updateRecentProject(project.id, ()=>project);
            if (isActiveProject) {
                setCurrentProjectDetail(previousCurrentProjectDetail);
            }
            Message.error(intl.get('file.error.renameFailed'));
            console.error('Failed to rename project:', error);
        });
    };
    const handleDeleteConfirm = async ()=>{
        if (!deleteModalProject) {
            return;
        }
        try {
            collect('website_delete', {
                project_id: deleteModalProject.id
            });
            await deleteProject(deleteModalProject.id);
            removeRecentProject(deleteModalProject.id);
            setHasTrashData(true);
            Message.success(intl.get('workspace.moveToTrash.success'));
            setDeleteModalProject(null);
        } catch (error) {
            Message.error(intl.get('workspace.moveToTrash.error', {
                error: error instanceof Error ? error.message : String(error)
            }));
            console.error('Failed to move project to trash:', error);
        }
    };
    const handleSidebarNavClick = (key, to)=>{
        collect('workspace_sidebar_click', {
            item: key,
            target_path: to
        });
    };
    const handleAnonymousLogin = ()=>{
        const shouldResumeLaunchOrder = Boolean(activeProjectId) && currentProjectId === activeProjectId && projectPanelMode === 'launch-order' && projectStatus === ProjectStatus.PENDING && Boolean(launchOrder);
        openGuestLoginModal('sidebar_footer', {
            onSuccess: shouldResumeLaunchOrder ? ()=>resumeLaunchOrderGenerationAfterLogin(currentProjectId, launchOrder) : undefined
        });
    };
    return /*#__PURE__*/ _jsxs("aside", {
        className: classNames(styles.sidebar, {
            [styles.collapsed]: isCollapsed,
            [styles['sidebar-overlay']]: overlayMode,
            [styles['sidebar-overlay-visible']]: overlayMode && visible,
            [styles['sidebar-project-overlay']]: projectOverlayMode,
            [styles['sidebar-project-overlay-visible']]: projectOverlayMode && projectOverlayVisible,
            [styles['sidebar-renaming']]: isProjectRenaming && !isCollapsed
        }),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: styles.header,
                children: isCollapsed ? /*#__PURE__*/ _jsx(Tooltip, {
                    content: intl.get('workspace.sidebar.open'),
                    position: "right",
                    triggerProps: {
                        mouseEnterDelay: 200
                    },
                    disabled: isMobile,
                    children: /*#__PURE__*/ _jsxs("button", {
                        className: classNames(styles['icon-button'], styles['collapsed-logo-button']),
                        type: "button",
                        "aria-label": intl.get('workspace.sidebar.open'),
                        "aria-expanded": !isCollapsed,
                        onClick: ()=>setCollapsed(false),
                        children: [
                            /*#__PURE__*/ _jsx(IconLogo, {
                                className: styles['compact-logo']
                            }),
                            /*#__PURE__*/ _jsx(IconLucidePanelLeft, {
                                className: styles['open-sidebar-icon'],
                                fontSize: 16
                            })
                        ]
                    })
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Tooltip, {
                            content: SIDEBAR_HOME_LABEL,
                            position: "bottom",
                            triggerProps: {
                                mouseEnterDelay: 800
                            },
                            disabled: isMobile,
                            children: /*#__PURE__*/ _jsx("a", {
                                className: classNames(styles['logo-link'], styles['logo-shine']),
                                href: "/",
                                target: "_blank",
                                rel: "noreferrer",
                                "aria-label": SIDEBAR_HOME_LABEL,
                                children: /*#__PURE__*/ _jsx(IconHeaderLogo24, {
                                    className: styles.logo
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(Tooltip, {
                            content: intl.get('workspace.sidebar.close'),
                            position: "bottom",
                            triggerProps: {
                                mouseEnterDelay: 200
                            },
                            disabled: isMobile,
                            children: /*#__PURE__*/ _jsx("button", {
                                className: styles['icon-button'],
                                type: "button",
                                "aria-label": intl.get('workspace.sidebar.close'),
                                "aria-expanded": !isCollapsed,
                                onClick: ()=>{
                                    if (projectOverlayMode) {
                                        onProjectOverlayClose === null || onProjectOverlayClose === void 0 ? void 0 : onProjectOverlayClose();
                                        return;
                                    }
                                    if (overlayMode) {
                                        onRequestClose === null || onRequestClose === void 0 ? void 0 : onRequestClose();
                                        return;
                                    }
                                    setCollapsed(true);
                                },
                                children: overlayMode || projectOverlayMode ? /*#__PURE__*/ _jsx(IconLucideX, {
                                    fontSize: 16
                                }) : /*#__PURE__*/ _jsx(IconLucidePanelLeft, {
                                    fontSize: 16
                                })
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("nav", {
                className: styles.nav,
                children: [
                    SIDEBAR_SECTIONS.map((section)=>/*#__PURE__*/ _jsxs("section", {
                            className: styles.section,
                            children: [
                                section.title && /*#__PURE__*/ _jsx("div", {
                                    className: styles['section-title'],
                                    children: section.title
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    className: styles.list,
                                    children: /*#__PURE__*/ _jsx(AnimatePresence, {
                                        initial: false,
                                        children: section.items.map((param)=>{
                                            let { key, labelKey, to, Icon } = param;
                                            if ((key === 'projects' || key === 'trash') && isAnonymousMode) {
                                                return null;
                                            }
                                            if (key === 'trash' && !shouldShowTrash) {
                                                return null;
                                            }
                                            return /*#__PURE__*/ _jsx(motion.li, {
                                                animate: disableNavItemMotion ? undefined : {
                                                    height: 42,
                                                    opacity: 1,
                                                    y: 0
                                                },
                                                className: styles['nav-list-item'],
                                                exit: disableNavItemMotion ? undefined : {
                                                    height: 0,
                                                    opacity: 0,
                                                    y: -4
                                                },
                                                initial: disableNavItemMotion ? false : {
                                                    height: 0,
                                                    opacity: 0,
                                                    y: -4
                                                },
                                                layout: disableNavItemMotion || isCollapsed ? false : 'position',
                                                transition: disableNavItemMotion ? undefined : {
                                                    duration: 0.24,
                                                    ease: 'easeOut'
                                                },
                                                children: /*#__PURE__*/ _jsxs(NavLink, {
                                                    end: true,
                                                    to: to,
                                                    onClick: ()=>handleSidebarNavClick(key, to),
                                                    className: (param)=>{
                                                        let { isActive } = param;
                                                        return classNames(styles.item, {
                                                            [styles.active]: isActive && key !== 'templates'
                                                        });
                                                    },
                                                    children: [
                                                        isCollapsed ? /*#__PURE__*/ _jsx(Tooltip, {
                                                            content: intl.get(labelKey),
                                                            position: "right",
                                                            triggerProps: {
                                                                mouseEnterDelay: 200
                                                            },
                                                            children: /*#__PURE__*/ _jsx(Icon, {
                                                                className: styles.icon,
                                                                fontSize: 16
                                                            })
                                                        }) : /*#__PURE__*/ _jsx(Icon, {
                                                            className: styles.icon,
                                                            fontSize: 16
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: styles.label,
                                                            children: intl.get(labelKey)
                                                        })
                                                    ]
                                                })
                                            }, key);
                                        })
                                    })
                                })
                            ]
                        }, section.key)),
                    /*#__PURE__*/ _jsx("section", {
                        className: classNames(styles.section, styles['chat-history-section']),
                        children: isCollapsed ? /*#__PURE__*/ _jsx(CollapsedChatHistory, {
                            activeProjectId: activeProjectId,
                            isAnyProjectRenaming: isProjectRenaming,
                            recentProjects: recentProjects,
                            renamingProjectId: renamingProjectId,
                            onDeleteClick: setDeleteModalProject,
                            onRenameCancel: handleRenameCancel,
                            onRenameSave: handleRenameSave,
                            onRenameStart: setRenamingProjectId
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Tooltip, {
                                    content: intl.get('workspace.sidebar.recentsTooltip'),
                                    position: "bl",
                                    triggerProps: {
                                        mouseEnterDelay: 200,
                                        popupAlign: {
                                            bottom: 8
                                        },
                                        boundaryDistance: {
                                            left: 10,
                                            bottom: 10
                                        }
                                    },
                                    children: recentProjects.length !== 0 && /*#__PURE__*/ _jsxs("div", {
                                        className: classNames(styles['section-title'], styles['recents-section-title']),
                                        children: [
                                            intl.get('workspace.sidebar.recents'),
                                            isAnonymousMode && /*#__PURE__*/ _jsx("span", {
                                                className: styles['recents-limit'],
                                                children: anonymousRecentProjectCountLabel
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(ScrollArea, {
                                    className: styles['chat-history-scroll-area'],
                                    scrollShadow: "bottom",
                                    children: [
                                        /*#__PURE__*/ _jsx(RecentProjectList, {
                                            activeProjectId: activeProjectId,
                                            isAnyProjectRenaming: isProjectRenaming,
                                            recentProjects: recentProjects,
                                            renamingProjectId: renamingProjectId,
                                            onDeleteClick: setDeleteModalProject,
                                            onRenameCancel: handleRenameCancel,
                                            onRenameSave: handleRenameSave,
                                            onRenameStart: setRenamingProjectId
                                        }),
                                        recentProjects.length === 0 && !isAnonymousMode && /*#__PURE__*/ _jsx("div", {
                                            className: styles['empty-recent'],
                                            children: intl.get('workspace.sidebar.emptyRecentProjects')
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles.footer,
                children: isAnonymousMode ? /*#__PURE__*/ _jsxs("div", {
                    className: classNames(styles['anonymous-login-button'], {
                        [styles['anonymous-login-button-collapsed']]: isCollapsed
                    }),
                    onClick: handleAnonymousLogin,
                    children: [
                        /*#__PURE__*/ _jsx(IconAnonymousUser, {}),
                        /*#__PURE__*/ _jsx("span", {
                            className: styles['anonymous-login-text'],
                            children: intl.get('common.user.notLogin')
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(UserButton, {
                            collapsed: isCollapsed,
                            settingsType: overlayMode || projectOverlayMode ? 'project' : 'global'
                        }),
                        /*#__PURE__*/ _jsx(NotificationButton, {
                            collapsed: isCollapsed
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Modal, {
                title: intl.get('workspace.moveToTrash.title'),
                description: deleteModalProject ? intl.get('workspace.moveToTrash.description', {
                    name: deleteModalProject.name
                }) : '',
                visible: Boolean(deleteModalProject),
                onOk: handleDeleteConfirm,
                onCancel: ()=>setDeleteModalProject(null),
                size: "m",
                cancelText: intl.get('common.action.cancel'),
                okText: intl.get('workspace.moveToTrash.ok'),
                okButtonProps: {
                    status: 'danger'
                },
                maskStyle: {
                    backdropFilter: 'blur(5px)'
                },
                unmountOnExit: true
            })
        ]
    });
}
export default Sidebar;
